import {
  fetchAreaDetail,
  fetchAreaFeed,
  fetchAreaSummaries,
  fetchAreasGeoJson,
  fetchHealth,
  fetchStatus,
  fetchUnmatched,
} from "./api";
import type { AreaFeature, AreaFeedResponse, HealthResponse, StatusResponse } from "./apiTypes";
import { rankAreaMatches, type AreaSearchCandidate } from "./areaSearch";
import { createBottomSheetController } from "./bottomSheet";
import { computeGlance, conditionCounts, GLANCE_CONDITIONS, type GlanceCondition } from "./glance";
import { runLocateFlow } from "./geolocation";
import { renderAreaFeed, renderAreaPanel, renderAreaPanelShell, renderAreaTimeline } from "./panel";
import { findContainingArea } from "./pointInPolygon";
import { createSearchController } from "./search";
import {
  clearStatusFilters,
  effectiveStatusFilters,
  ensureAppShell,
  featureDisplayName,
  isLegendOpen,
  mergeStatusIntoFeatures,
  renderAreaList,
  renderFreshness,
  renderHealthPanel,
  renderUnmappedAreas,
  setLegendOpen,
  syncFilterState,
  updateDirectoryCount,
  updateLegendCounts,
  updateSheetSummary,
  updateStaleBanner,
  type SheetSelection,
} from "./shell";
import { updateRelativeLabels } from "./timeline";
import { renderUnmatchedPanel } from "./unmatched";
import { buildUpNext, renderUpNext } from "./upNext";
import type { MapController } from "./map";

export const STATUS_REFRESH_BASE_MS = 120_000;
export const STATUS_REFRESH_JITTER_MS = 15_000;

export function nextStatusRefreshDelay(random = Math.random): number {
  return STATUS_REFRESH_BASE_MS + Math.round((random() * 2 - 1) * STATUS_REFRESH_JITTER_MS);
}

export function parseAreaHash(hash: string): string | null {
  if (!hash.startsWith("#")) {
    return null;
  }
  const key = new URLSearchParams(hash.slice(1)).get("area");
  return key && key.length > 0 ? key : null;
}

export function areaHash(key: string | null): string {
  return key ? `#area=${encodeURIComponent(key)}` : "";
}

function historyUrlForArea(key: string | null): string {
  return `${window.location.pathname}${window.location.search}${areaHash(key)}`;
}

function isPhoneLayout(): boolean {
  return typeof window.matchMedia === "function" && window.matchMedia("(max-width: 767px)").matches;
}

function emptyFeed(areaKey: string): AreaFeedResponse {
  return { generatedAt: new Date().toISOString(), areaKey, items: [] };
}

function insertClearSelectionButton(panel: HTMLElement): void {
  const card = panel.querySelector(".area-panel-card") ?? panel;
  if (card.querySelector("[data-clear-selection]")) {
    return;
  }
  const button = document.createElement("button");
  button.type = "button";
  button.dataset.clearSelection = "";
  button.className = "clear-selection";
  button.textContent = "Show all areas";
  card.insertBefore(button, card.firstChild);
}

function showApiError(elements: ReturnType<typeof ensureAppShell>, message = "Map data could not load."): void {
  elements.apiError.removeAttribute("hidden");
  const paragraph = elements.apiError.querySelector("p");
  if (paragraph) {
    paragraph.textContent = message;
  }
}

function hideApiError(elements: ReturnType<typeof ensureAppShell>): void {
  elements.apiError.setAttribute("hidden", "");
  elements.map.textContent = "";
}

export async function startApp(root: HTMLElement): Promise<void> {
  const elements = ensureAppShell(root);
  const bottomSheet = createBottomSheetController(elements.bottomSheet);
  let features: AreaFeature[] = [];
  let statusResponse: StatusResponse | null = null;
  let healthResponse: HealthResponse | null = null;
  let mapController: MapController | null = null;
  let refreshTimer: number | undefined;
  let legendCounts: Partial<Record<GlanceCondition, number>> = {};
  let selected: SheetSelection | undefined;
  let selectedKey: string | null = null;
  const names = new Map<string, string>();

  function nameFor(key: string): string {
    return names.get(key) ?? key;
  }

  function rememberFeatureNames(): void {
    for (const feature of features) {
      if (!names.has(feature.properties.key)) {
        names.set(feature.properties.key, featureDisplayName(feature));
      }
    }
  }

  function selectionForKey(key: string, now = new Date()): SheetSelection {
    const feature = features.find((candidate) => candidate.properties.key === key);
    const summary = statusResponse?.areas.find((area) => area.key === key);
    const source = feature?.properties ?? summary ?? { status: "unknown" as const };
    const glance = computeGlance(source, now);
    return {
      name: feature ? featureDisplayName(feature) : nameFor(key),
      statusLabel: glance.label,
      tier: glance.tier,
      countdown: glance.countdown ?? undefined,
    };
  }

  function refreshSheetSummary(now = new Date()): void {
    if (selectedKey) {
      selected = selectionForKey(selectedKey, now);
    }
    updateSheetSummary(elements.sheetSummaryText, legendCounts, selected, now);
  }

  function knownAreaKey(key: string): boolean {
    return (
      features.some((feature) => feature.properties.key === key) ||
      Boolean(statusResponse?.areas.some((area) => area.key === key))
    );
  }

  function pushAreaHistory(key: string | null): void {
    const url = historyUrlForArea(key);
    if (`${window.location.pathname}${window.location.search}${window.location.hash}` === url) {
      return;
    }
    window.history.pushState(null, "", url);
  }

  function applyAreaHash(hash: string): void {
    const key = parseAreaHash(hash);
    if (!key) {
      clearAreaSelection({ pushHistory: false });
      return;
    }
    if (!knownAreaKey(key)) {
      return;
    }
    void selectArea(key, { pushHistory: false });
  }

  async function selectArea(key: string, options: { pushHistory?: boolean } = {}): Promise<void> {
    if (options.pushHistory ?? true) {
      pushAreaHistory(key);
    }
    selectedKey = key;
    selected = selectionForKey(key);
    refreshSheetSummary();
    bottomSheet.set("half");
    elements.areaPanel.removeAttribute("hidden");
    elements.globalViews.setAttribute("hidden", "");
    renderAreaPanelShell(elements.areaPanel, key);
    insertClearSelectionButton(elements.areaPanel);
    elements.areaPanel.scrollIntoView({ block: "start" });
    elements.areaPanel.focus({ preventScroll: true });
    mapController?.focusArea(key);

    try {
      const [detail, feed] = await Promise.all([
        fetchAreaDetail(key),
        fetchAreaFeed(key).catch(() => emptyFeed(key)),
      ]);
      if (selectedKey !== key) {
        return;
      }
      const now = new Date();
      names.set(key, detail.area.displayName || nameFor(key));
      const glance = computeGlance(
        {
          status: detail.area.status.value,
          stale: detail.area.status.stale,
          lastKnownStatus: detail.area.status.lastKnownStatus,
          activeWindow: detail.area.status.activeWindow,
          nextWindow: detail.area.status.nextWindow,
        },
        now,
      );
      selected = {
        name: detail.area.displayName,
        statusLabel: glance.label,
        tier: glance.tier,
        countdown: glance.countdown ?? undefined,
      };
      updateSheetSummary(elements.sheetSummaryText, legendCounts, selected, now);
      renderAreaPanel(elements.areaPanel, detail.area, now);
      insertClearSelectionButton(elements.areaPanel);
      renderAreaTimeline(elements.areaPanel, detail.area, feed, now);
      renderAreaFeed(elements.areaPanel, feed);
      updateRelativeLabels(elements.areaPanel, now);
    } catch {
      elements.areaPanel.textContent = "";
      insertClearSelectionButton(elements.areaPanel);
      const message = document.createElement("p");
      message.className = "api-error-inline";
      message.textContent = "Area details could not load.";
      elements.areaPanel.appendChild(message);
    }
  }

  function clearAreaSelection(options: { pushHistory?: boolean } = {}): void {
    if (options.pushHistory ?? true) {
      pushAreaHistory(null);
    }
    selected = undefined;
    selectedKey = null;
    elements.areaPanel.setAttribute("hidden", "");
    elements.globalViews.removeAttribute("hidden");
    refreshSheetSummary();
    mapController?.clearSelection();
    mapController?.resetView();
    bottomSheet.set("collapsed");
  }

  function wireAreaButtons(): void {
    for (const container of [elements.areaList, elements.unmappedPanel, elements.upNext]) {
      container.addEventListener("click", (event) => {
        const button = (event.target as HTMLElement).closest<HTMLButtonElement>("[data-area-key]");
        if (button?.dataset.areaKey) {
          void selectArea(button.dataset.areaKey);
        }
      });
    }
  }

  function areaFilterQuery(): string {
    return root.querySelector<HTMLInputElement>("[data-area-filter]")?.value ?? "";
  }

  function renderDirectory(filters = effectiveStatusFilters(root), now = new Date()): void {
    renderAreaList(elements.areaList, features, filters, areaFilterQuery(), now);
  }

  function renderLists(now = new Date()): void {
    const filters = effectiveStatusFilters(root);
    renderDirectory(filters, now);
    if (statusResponse) {
      renderUnmappedAreas(elements.unmappedPanel, statusResponse, names, now);
      renderUpNext(elements.upNext, buildUpNext(statusResponse.areas, names, now), now);
    }
    renderFreshness(elements.freshness, healthResponse, statusResponse, now);
  }

  function applyStatus(status: StatusResponse): void {
    const now = new Date();
    statusResponse = status;
    features = mergeStatusIntoFeatures(features, status);
    rememberFeatureNames();
    legendCounts = conditionCounts(status.areas, now);
    updateLegendCounts(root, legendCounts);
    updateDirectoryCount(root, status);
    refreshSheetSummary(now);
    renderLists(now);
    mapController?.tick(now);
    mapController?.setFeatures(features);
    mapController?.setStatusFilters(effectiveStatusFilters(root));
  }

  function minuteTick(): void {
    const now = new Date();
    if (statusResponse) {
      legendCounts = conditionCounts(statusResponse.areas, now);
      updateLegendCounts(root, legendCounts);
    }
    refreshSheetSummary(now);
    renderLists(now);
    updateRelativeLabels(root, now);
    mapController?.tick(now);
  }

  async function refreshStatusAndHealth(): Promise<void> {
    const [status, health] = await Promise.all([fetchStatus(), fetchHealth()]);
    healthResponse = health;
    applyStatus(status);
    updateStaleBanner(elements.staleBanner, health);
    renderHealthPanel(elements.healthPanel, health);
  }

  function scheduleRefresh(): void {
    window.clearTimeout(refreshTimer);
    if (document.hidden) {
      return;
    }
    refreshTimer = window.setTimeout(() => {
      void refreshStatusAndHealth().finally(scheduleRefresh);
    }, nextStatusRefreshDelay());
  }

  async function loadNames(): Promise<void> {
    try {
      const summaries = await fetchAreaSummaries();
      for (const area of summaries.areas) {
        if (area.displayName) {
          names.set(area.key, area.displayName);
        }
      }
    } catch {
      // Names fall back to feature names or keys.
    }
  }

  async function loadInitial(): Promise<void> {
    hideApiError(elements);
    try {
      const [areas, health] = await Promise.all([fetchAreasGeoJson(), fetchHealth(), loadNames()]);
      features = areas.features;
      healthResponse = health;
      rememberFeatureNames();
      updateStaleBanner(elements.staleBanner, health);
      renderHealthPanel(elements.healthPanel, health);

      try {
        applyStatus(await fetchStatus());
      } catch {
        renderAreaList(elements.areaList, features, new Set(GLANCE_CONDITIONS), areaFilterQuery());
        renderFreshness(elements.freshness, health, null);
      }

      if (!mapController) {
        const { createMap } = await import("./map");
        mapController = createMap(elements.map, {
          features,
          phonePadding: isPhoneLayout(),
          onSelect(feature) {
            void selectArea(feature.properties.key);
          },
        });
        mapController.setStatusFilters(effectiveStatusFilters(root));
      } else {
        mapController.setFeatures(features);
      }

      try {
        renderUnmatchedPanel(elements.unmatchedPanel, await fetchUnmatched());
      } catch {
        elements.unmatchedPanel.textContent = "Unmatched updates could not load.";
      }
      scheduleRefresh();
    } catch {
      showApiError(elements);
    }
  }

  function searchCandidates(now = new Date()): AreaSearchCandidate[] {
    const candidates: AreaSearchCandidate[] = [];
    const seen = new Set<string>();
    for (const feature of features) {
      const glance = computeGlance(feature.properties, now);
      candidates.push({
        key: feature.properties.key,
        name: featureDisplayName(feature),
        tier: glance.tier,
        tierLabel: glance.label,
        mapped: true,
      });
      seen.add(feature.properties.key);
    }
    for (const area of statusResponse?.areas ?? []) {
      if (seen.has(area.key)) {
        continue;
      }
      const glance = computeGlance(area, now);
      candidates.push({ key: area.key, name: nameFor(area.key), tier: glance.tier, tierLabel: glance.label, mapped: area.mapped });
    }
    return candidates;
  }

  elements.retryButton.addEventListener("click", () => {
    void loadInitial();
  });

  function applyFilters(): void {
    syncFilterState(root);
    const filters = effectiveStatusFilters(root);
    renderDirectory(filters);
    mapController?.setStatusFilters(filters);
  }

  elements.legend.addEventListener("change", applyFilters);
  root.querySelector("[data-legend-clear]")?.addEventListener("click", () => {
    clearStatusFilters(root);
    applyFilters();
  });

  root.querySelector("[data-area-filter]")?.addEventListener("input", () => {
    renderDirectory();
  });

  elements.legendToggle.addEventListener("click", () => {
    setLegendOpen(elements.legend, !isLegendOpen(elements.legend));
  });
  elements.legend.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && isLegendOpen(elements.legend)) {
      setLegendOpen(elements.legend, false);
      elements.legendToggle.focus();
    }
  });

  createSearchController(elements.searchInput, elements.searchStatus, elements.searchResults, {
    localSearch(query) {
      return rankAreaMatches(query, searchCandidates());
    },
    onSelectLocal(result) {
      void selectArea(result.key);
    },
    onSelect(result) {
      const point: [number, number] = [result.lat, result.lon];
      mapController?.setView(point);
      const area = findContainingArea(point, features);
      if (area) {
        void selectArea(area.properties.key);
      } else {
        elements.searchStatus.textContent = "Address found, but it is outside the mapped outage areas. Try Locate me.";
      }
    },
  });

  elements.locateButton.addEventListener("click", () => {
    void runLocateFlow(features, {
      onState(result) {
        elements.locateStatus.textContent = result.message;
        elements.locateStatus.dataset.state = result.state;
      },
    }).then((result) => {
      if (result.state === "success" && result.area) {
        void selectArea(result.area.properties.key);
      }
    });
  });

  document.addEventListener("visibilitychange", scheduleRefresh);
  wireAreaButtons();
  elements.areaPanel.addEventListener("click", (event) => {
    if ((event.target as HTMLElement).closest("[data-clear-selection]")) {
      clearAreaSelection();
    }
  });
  window.addEventListener("popstate", () => {
    if (!root.isConnected) {
      return;
    }
    applyAreaHash(window.location.hash);
  });
  window.setInterval(minuteTick, 60_000);
  await loadInitial();
  const initialKey = parseAreaHash(window.location.hash);
  if (initialKey && knownAreaKey(initialKey)) {
    await selectArea(initialKey, { pushHistory: false });
  }
}

const autoRoot = typeof document !== "undefined" ? document.getElementById("app") : null;
if (autoRoot && !autoRoot.hasAttribute("data-disable-auto-start")) {
  void startApp(autoRoot);
}
